// Keycloak Module — monitoring a správa přes Admin API
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import {
  Card,
  CardContent,
  PageHeader,
  Select,
  StatCard,
  StatsGrid,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from '../../components/ui';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Globe,
  RefreshCw,
  Search,
  Shield,
  Users,
  XCircle,
} from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────────────

interface KcRealm {
  realm: string;
  displayName?: string;
  enabled: boolean;
  users?: number;
}

interface KcUser {
  id: string;
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  enabled: boolean;
  created: string | null;
  source?: string;
}

interface KcClient {
  id: string;
  clientId: string;
  name?: string;
  enabled: boolean;
  protocol: string;
  publicClient: boolean;
  rootUrl?: string;
}

// ── Status Tab ────────────────────────────────────────────────────────────────

function StatusTab() {
  const { t } = useTranslation();
  // sso.keycloak_status = health + realm summary (users count) v jednom toolu
  const { data: status, isLoading, refetch } = useQuery({
    queryKey: ['kc-status'],
    queryFn: () =>
      api.mcp<{ ok: boolean; ms?: number; realms?: KcRealm[]; error?: string }>(
        'sso.keycloak_status',
        {},
      ),
    refetchInterval: 30_000,
  });

  const health = status
    ? { ok: !!status.ok, ms: status.ms ?? 0 }
    : undefined;
  const realms = status?.realms ?? [];

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-3'>
          {health
            ? (
              health.ok
                ? (
                  <div className='flex items-center gap-2 text-green-400'>
                    <CheckCircle2 size={16} />
                    <span className='text-sm font-medium'>Online</span>
                    <span className='text-xs text-gray-500'>{health.ms} ms</span>
                  </div>
                )
                : (
                  <div className='flex items-center gap-2 text-red-400'>
                    <XCircle size={16} />
                    <span className='text-sm font-medium'>{t('keycloak.unavailable')}</span>
                  </div>
                )
            )
            : <div className='text-gray-500 text-sm'>{t('keycloak.checking')}</div>}
        </div>
        <button
          onClick={() => refetch()}
          className='p-2 rounded-lg border border-gray-700 text-gray-400 hover:text-white transition-colors'
        >
          <RefreshCw size={14} />
        </button>
      </div>

      <StatsGrid cols={3}>
        <StatCard label={t('keycloak.realms')} value={realms.length} icon={Globe} />
        <StatCard
          label='Response time'
          value={health?.ms ? `${health.ms} ms` : '—'}
          icon={Clock}
          variant={health?.ms && health.ms < 200
            ? 'success'
            : health?.ms && health.ms < 500
            ? 'warning'
            : 'error'}
        />
        <StatCard
          label='Status'
          value={health?.ok ? 'Online' : 'Offline'}
          icon={Shield}
          variant={health?.ok ? 'success' : 'error'}
        />
      </StatsGrid>

      {isLoading
        ? (
          <div className='space-y-2'>
            {[1, 2].map((i) => (
              <div key={i} className='h-16 bg-gray-800/40 rounded-xl animate-pulse' />
            ))}
          </div>
        )
        : (
          <div className='space-y-3'>
            {realms.map((r) => (
              <Card key={r.realm}>
                <CardContent>
                  <div className='flex items-center justify-between py-1'>
                    <div className='flex items-center gap-3'>
                      <div
                        className={`w-2 h-2 rounded-full ${
                          r.enabled ? 'bg-green-400' : 'bg-red-400'
                        }`}
                      />
                      <div>
                        <p className='text-white font-mono font-semibold text-sm'>{r.realm}</p>
                        {r.displayName && <p className='text-xs text-gray-500'>{r.displayName}</p>}
                      </div>
                    </div>
                    <div className='flex items-center gap-4 text-xs text-gray-500'>
                      {r.users !== undefined && (
                        <span className='flex items-center gap-1'>
                          <Users size={11} />
                          {t('keycloak.usersCount', { count: r.users })}
                        </span>
                      )}
                      <span className={r.enabled ? 'text-green-400' : 'text-red-400'}>
                        {r.enabled ? 'enabled' : 'disabled'}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
    </div>
  );
}

// ── Users Tab ─────────────────────────────────────────────────────────────────

function UsersTab() {
  const { t } = useTranslation();
  const [realm, setRealm] = useState('master');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [first, setFirst] = useState(0);
  const PAGE = 40;

  const { data: realmsData } = useQuery({
    queryKey: ['kc-status'],
    queryFn: () =>
      api.mcp<{ ok: boolean; realms?: KcRealm[] }>('sso.keycloak_status', {}),
  });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['kc-users', realm, search, first],
    queryFn: () =>
      api.mcp<{ ok: boolean; users: KcUser[]; total: number }>(
        'sso.keycloak_list_users',
        { realm, search: search || undefined, first, max: PAGE },
      ),
  });

  const importMut = useMutation({
    mutationFn: (u: KcUser) =>
      api.mcp<{ ok?: boolean; existed?: boolean; error?: string }>(
        'identity.user_create',
        { label: u.username, keycloak_sub: u.id, keycloak_username: u.username },
      ),
    onSuccess: (r, u) => {
      if (r?.ok === false) {
        toast.error(String(r.error || t('keycloak.importFailed', 'Import selhal')));
        return;
      }
      toast.success(
        r?.existed
          ? t('keycloak.alreadyImported', '{{user}} už v ACM je', { user: u.username })
          : t('keycloak.imported', 'Importováno {{user}} — založena lokální entita', {
            user: u.username,
          }),
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const realms = realmsData?.realms ?? [];
  const users = data?.users ?? [];

  return (
    <div className='space-y-4'>
      <div className='flex items-center gap-3 flex-wrap'>
        <Select
          value={realm}
          onChange={(e) => setRealm(e.target.value)}
        >
          {realms.length
            ? realms.map((r) => <option key={r.realm} value={r.realm}>{r.realm}</option>)
            : <option value='master'>master</option>}
        </Select>

        <div className='relative flex-1 min-w-48'>
          <Search size={13} className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-500' />
          <input
            className='w-full bg-gray-900/60 border border-gray-700/60 rounded-xl pl-8 pr-3 py-2 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-blue-500/50'
            placeholder={t('keycloak.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setFirst(0);
                setSearch(searchInput);
              }
            }}
          />
        </div>
        <button
          onClick={() => {
            setFirst(0);
            setSearch(searchInput);
          }}
          className='px-3 py-2 rounded-xl bg-blue-600/20 border border-blue-500/30 text-blue-300 text-sm hover:bg-blue-600/30 transition-colors'
        >
          {t('keycloak.search')}
        </button>
        <button
          onClick={() => refetch()}
          className='p-2 rounded-lg border border-gray-700 text-gray-400 hover:text-white transition-colors'
        >
          <RefreshCw size={14} />
        </button>
      </div>

      <div className='flex items-center justify-between text-xs text-gray-500'>
        <span>{t('keycloak.usersCount', { count: data?.total ?? 0 })}</span>
        <span className='flex gap-3'>
          <button
            type='button'
            className='text-blue-400 disabled:text-gray-600'
            disabled={first <= 0}
            onClick={() => setFirst((n) => Math.max(0, n - PAGE))}
          >
            {t('common.prev', 'Předchozí')}
          </button>
          <button
            type='button'
            className='text-blue-400 disabled:text-gray-600'
            disabled={(data?.users.length ?? 0) < PAGE}
            onClick={() => setFirst((n) => n + PAGE)}
          >
            {t('common.next', 'Další')}
          </button>
        </span>
      </div>

      {isLoading
        ? (
          <div className='space-y-2'>
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className='h-12 bg-gray-800/40 rounded-xl animate-pulse' />
            ))}
          </div>
        )
        : isError
        ? (
          <div className='flex items-center gap-2 text-red-400 text-sm p-4 bg-red-500/10 rounded-xl border border-red-500/20'>
            <AlertCircle size={14} /> {t('keycloak.usersLoadError')}
          </div>
        )
        : (
          <div className='border border-gray-800 rounded-xl overflow-hidden'>
            <table className='w-full text-sm'>
              <thead>
                <tr className='border-b border-gray-800 bg-gray-900/40'>
                  {['Username', 'Email', t('keycloak.colSource', 'Zdroj'), t('keycloak.colStatus'), t('keycloak.colCreated'), ''].map((h) => (
                    <th key={h} className='text-left px-4 py-2.5 text-xs font-medium text-gray-500'>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-800/40'>
                {users.length === 0 && (
                  <tr>
                    <td colSpan={6} className='px-4 py-8 text-center text-gray-600 text-xs'>
                      {t('keycloak.noUsers')}
                    </td>
                  </tr>
                )}
                {users.map((u) => (
                  <tr key={u.id} className='hover:bg-gray-800/20 transition-colors'>
                    <td className='px-4 py-2.5'>
                      <span className='font-mono text-white text-xs'>{u.username}</span>
                    </td>
                    <td className='px-4 py-2.5 text-xs text-gray-400'>{u.email ?? '—'}</td>
                    <td className='px-4 py-2.5 text-xs text-gray-500'>{u.source ?? 'keycloak'}</td>
                    <td className='px-4 py-2.5'>
                      {u.enabled
                        ? (
                          <span className='flex items-center gap-1 text-green-400 text-xs'>
                            <CheckCircle2 size={10} />{t('keycloak.active')}
                          </span>
                        )
                        : (
                          <span className='flex items-center gap-1 text-red-400 text-xs'>
                            <XCircle size={10} />disabled
                          </span>
                        )}
                    </td>
                    <td className='px-4 py-2.5 text-xs text-gray-500'>
                      {u.created ? new Date(u.created).toLocaleDateString('cs') : '—'}
                    </td>
                    <td className='px-4 py-2.5 text-right'>
                      <button
                        type='button'
                        className='text-xs text-blue-400 hover:text-blue-300 disabled:text-gray-600'
                        disabled={importMut.isPending}
                        onClick={() => importMut.mutate(u)}
                      >
                        {t('keycloak.importToAcm', 'Importovat do ACM')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}

// ── Clients Tab ───────────────────────────────────────────────────────────────

function ClientsTab() {
  const { t } = useTranslation();
  const [realm, setRealm] = useState('master');

  const { data: realmsData } = useQuery({
    queryKey: ['kc-status'],
    queryFn: () =>
      api.mcp<{ ok: boolean; realms?: KcRealm[] }>('sso.keycloak_status', {}),
  });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['kc-clients', realm],
    queryFn: () =>
      api.mcp<{ ok: boolean; clients: KcClient[]; total: number }>(
        'sso.keycloak_list_clients',
        { realm },
      ),
  });

  const realms = realmsData?.realms ?? [];
  const clients = (data?.clients ?? []).filter((c) => !c.clientId.startsWith('broker'));

  return (
    <div className='space-y-4'>
      <div className='flex items-center gap-3'>
        <Select
          value={realm}
          onChange={(e) => setRealm(e.target.value)}
        >
          {realms.length
            ? realms.map((r) => <option key={r.realm} value={r.realm}>{r.realm}</option>)
            : <option value='master'>master</option>}
        </Select>
        <button
          onClick={() => refetch()}
          className='p-2 rounded-lg border border-gray-700 text-gray-400 hover:text-white transition-colors'
        >
          <RefreshCw size={14} />
        </button>
        <span className='text-xs text-gray-500 ml-auto'>{t('keycloak.clientsCount', { count: clients.length })}</span>
      </div>

      {isLoading
        ? (
          <div className='space-y-2'>
            {[1, 2, 3].map((i) => (
              <div key={i} className='h-12 bg-gray-800/40 rounded-xl animate-pulse' />
            ))}
          </div>
        )
        : isError
        ? (
          <div className='flex items-center gap-2 text-red-400 text-sm p-4 bg-red-500/10 rounded-xl border border-red-500/20'>
            <AlertCircle size={14} /> {t('keycloak.clientsLoadError')}
          </div>
        )
        : (
          <div className='border border-gray-800 rounded-xl overflow-hidden'>
            <table className='w-full text-sm'>
              <thead>
                <tr className='border-b border-gray-800 bg-gray-900/40'>
                  {['Client ID', t('keycloak.colName'), t('keycloak.colProtocol'), t('keycloak.colType'), t('keycloak.colStatus'), 'Root URL'].map((h) => (
                    <th key={h} className='text-left px-4 py-2.5 text-xs font-medium text-gray-500'>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-800/40'>
                {clients.map((c) => (
                  <tr key={c.id} className='hover:bg-gray-800/20 transition-colors'>
                    <td className='px-4 py-2.5'>
                      <code className='text-xs text-blue-300 font-mono'>{c.clientId}</code>
                    </td>
                    <td className='px-4 py-2.5 text-xs text-gray-400'>{c.name ?? '—'}</td>
                    <td className='px-4 py-2.5 text-xs text-gray-500'>{c.protocol}</td>
                    <td className='px-4 py-2.5 text-xs text-gray-500'>
                      {c.publicClient ? 'public' : 'confidential'}
                    </td>
                    <td className='px-4 py-2.5'>
                      {c.enabled
                        ? (
                          <span className='flex items-center gap-1 text-green-400 text-xs'>
                            <CheckCircle2 size={10} />enabled
                          </span>
                        )
                        : (
                          <span className='flex items-center gap-1 text-red-400 text-xs'>
                            <XCircle size={10} />disabled
                          </span>
                        )}
                    </td>
                    <td className='px-4 py-2.5 text-xs text-gray-500 max-w-xs truncate'>
                      {c.rootUrl ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

// ── KC Instances tab ──────────────────────────────────────────────────────────


export default function KeycloakModule() {
  const { t } = useTranslation();
  return (
    <div className='space-y-6'>
      <PageHeader
        title='Keycloak SSO'
        description={t('keycloak.description')}
        icon={Shield}
      />

      <Tabs defaultTab='status'>
        <TabList>
          <Tab id='status' icon={<Shield size={14} />}>{t('keycloak.statusTab')}</Tab>
          <Tab id='users' icon={<Users size={14} />}>{t('keycloak.usersTab')}</Tab>
          <Tab id='clients' icon={<Globe size={14} />}>{t('keycloak.clientsTab')}</Tab>
        </TabList>

        <div className='mt-6'>
          <TabPanel id='status'>
            <StatusTab />
          </TabPanel>
          <TabPanel id='users'>
            <UsersTab />
          </TabPanel>
          <TabPanel id='clients'>
            <ClientsTab />
          </TabPanel>
        </div>
      </Tabs>
    </div>
  );
}
